/**
 * client/src/home/verticals.ts — the three verticals, and the one address a brief is built at.
 *
 * ================================================================================================
 * HOW A VERTICAL REACHES THE BUILDER TODAY: IT DOES NOT. MEASURED, NOT ASSUMED.
 * ================================================================================================
 *
 * This was read out of the code before the chooser above it was designed, because the obvious
 * design — three cards that each start a different kind of brief — describes a product this one is
 * not yet.
 *
 *   1. THE BUILDER'S ONLY ENTRY POINT TAKES A TIER, NOT A VERTICAL. `/cma` is `cma/CmaAmbFlow`,
 *      and the call it finishes on is `cma/api.ts::createJob` -> `POST /api/amb/jobs`. That body is
 *      `amb_report.JobIn` (src/server/amb_report.py:193): `subject`, `comps`, `tier`, `meta`,
 *      `images`, `source_job_id`. There is no `vertical` field on it and never has been.
 *
 *   2. `tier` AND `vertical` ARE DIFFERENT AXES. `cma/constants.ts::REPORT_TIERS` is
 *      `["cma_calc", "cma_ai", "amb"]` — how much narrative work runs. `vertical` is a column on
 *      two tables: `orgs.default_vert` (src/server/db.py:37) and `analyses.vertical`
 *      (src/server/db.py:118), both defaulting to `"protest"`.
 *
 *   3. THE ONLY WRITERS OF `analyses.vertical` ARE LEGACY ROUTES THIS CLIENT NEVER CALLS.
 *      `POST /api/analyze` takes `vertical: str = "protest"` (src/server/app.py:385, 401 -> 1110,
 *      1138) and the BPO routes hardcode `vertical="bpo"` (app.py:1266, projects.py:572).
 *      `/next` calls neither. Nothing in `client/src` sends the word at all.
 *
 *   4. THE SHELF CONFIRMS IT FROM THE OTHER END. `crm_next/report_sync.py` files a brief by
 *      reading `amb_jobs.tier` through `_TIER_TO_KIND` (line 44) — so every brief this client
 *      builds lands on the shelf as `cma` or `amb`. `_VERTICAL_TO_KIND` (line 49) is the OTHER
 *      store's mapping, for rows `/next` does not create.
 *
 *   5. THERE ARE NO `/protest` OR `/bpo` ROUTES. `App.tsx` serves `/cma`, `/briefs`, `/crm/*`,
 *      `/method`, `/branding`, `/extension`, `/users`, `/appraisal` and the auth screens. The
 *      appraisal WORKSPACE at `/appraisal` reads a finished brief (`/api/next/appraisal/jobs/{id}`)
 *      — it is a second view of a brief, not a second way to build one.
 *
 * WHAT THAT MEANS FOR THE CHOOSER, AND WHY IT IS NOT A DEAD CONTROL. The three verticals are the
 * product's architecture and the charter's rule is that none of them is primary, so all three are
 * on the front door, side by side, in identical chrome. But a card that appeared to record a
 * choice the wire has no field for would be a control that silently does nothing — the exact thing
 * CLAUDE.md §8 forbids. So every card states the same action in the same words ("Opens the
 * builder"), the chooser says in one line that one builder serves all three, and nothing pretends
 * a selection was filed. Three named doors to one room, with the room named on each door.
 *
 * THE FOLLOW-UP THIS EXPOSES, NOT TAKEN HERE. Carrying the chosen vertical onto the job would mean
 * a field on `JobIn`, a column read on the shelf, and a decision about what it changes in the
 * output. That is a server contract change and a product decision, not a front-end one — see the
 * slice report.
 */

/**
 * The builder. A PLAIN PATH, used in a plain `<a href>` rather than a router push, for the reason
 * `ui/AppShell`'s "New analysis" gives: starting fresh must drop every scrap of the previous job's
 * in-memory state, which a hard navigation guarantees and a client-side route change does not.
 */
export const BUILDER_PATH = "/cma";

export interface Vertical {
  /** Stable id. Matches the words the server's own `vertical`/`kind` columns use where one
   *  exists — `protest`, `bpo`, `appraisal_support` are all in `crm_next/models.py`'s
   *  `REPORT_KINDS` — so the day a vertical does reach the wire, this list is not a third
   *  vocabulary. */
  id: "protest" | "bpo" | "appraisal_support";
  /** What it is called on screen. US English. */
  name: string;
  /** Who reads the brief. One sentence, no promise about what the builder does differently. */
  audience: string;
}

/**
 * ORDER IS NOT RANK. The list is rendered in one row, in identical chrome, with no primary
 * styling on any member — the charter's standing rule ("never build as if one vertical is
 * primary") is enforced by a test over this array AND over the rendered cards, because an order
 * is the one thing a reader will read as a ranking if the visual weight does not say otherwise.
 */
export const VERTICALS: readonly Vertical[] = [
  {
    id: "protest",
    name: "Property-tax protest",
    audience: "For the appraisal district and the review board.",
  },
  {
    id: "bpo",
    name: "Broker price opinion",
    audience: "For a lender, an asset manager or a seller.",
  },
  {
    id: "appraisal_support",
    name: "Appraisal support",
    audience: "For an appraiser's workfile.",
  },
] as const;

/** The same words on all three cards, so the shared action is legible rather than implied. */
export const VERTICAL_ACTION = "Opens the builder";
